import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RoomContext from '../lib/RoomClientContext';
import { setMicSound } from '../store/slices/deviceSlice';
import { setShowTalkingPopover } from '../store/slices/controlSlice';

const POPOVER_LIFETIME = 10 * 1000; // 10 seconds

function useAudioListener() {
  const dispatch = useDispatch();
  const micSound = useSelector((state) => state.device.micSound);
  const muted = useSelector((state) => state.control.muted);
  const roomClient = useContext(RoomContext);
  const audioManipulator = roomClient.audioManipulator;

  // Function to trigger and then auto-hide the talking popover
  const _setShowTalkingPopover = useCallback(() => {
    if (roomClient.triggeredTalkingPopover) {
      return;
    }
    roomClient.triggeredTalkingPopover = true;
    dispatch(setShowTalkingPopover(true));

    // Auto-hide the talking popover after timeout
    setTimeout(() => {
      dispatch(setShowTalkingPopover(false));
    }, POPOVER_LIFETIME);
  }, [dispatch, roomClient]);

  // Effect for handling micSound changes:
  // When micSound becomes true, show the talking popover.
  useEffect(() => {
    if (micSound) {
      _setShowTalkingPopover();
    }
  }, [micSound, _setShowTalkingPopover]);

  // Effect for setting up (or removing) the voice event listener based on muted state.
  useEffect(() => {
    if (!audioManipulator) return;

    // When muted is true, add the voice event listener.
    if (muted) {
      audioManipulator.activateVad();
      const voiceHandler = (val) => {
        // Only dispatch if talking popover hasn't been triggered already.
        if (!roomClient.triggeredTalkingPopover) {
          dispatch(setMicSound(val));
        }
      };
      audioManipulator.on('voice', voiceHandler);

      // Cleanup: remove the specific voice event listener.
      return () => {
        audioManipulator.removeListener('voice', voiceHandler);
      };
    } else {
      // When not muted, ensure micSound is reset to false and remove any voice listeners.
      dispatch(setMicSound(false));
      audioManipulator.removeAllListeners('voice');
    }
  }, [audioManipulator, muted, dispatch, roomClient]);
}

export default useAudioListener;
